/** Autentificare (App-Autentificare.dc.html) — passwordless-first: email→OTP, Google, passkey, 2FA. */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, humanError } from '../api/client';
import { sendOtp, verifyOtp, verifyTotp } from '../api/auth';
import { getMe } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Logo } from '../components/Logo';
import { Spinner } from '../components/ui';
import { formatMmSs } from '../lib/format';

type View = 'login' | 'register' | 'otp' | 'twofa' | 'passkey';
const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'http://localhost:4603';

export function LoginPage() {
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    document.title = 'Intră în cont — Reclame Video';
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [resendIn]);

  async function finishLogin() {
    await qc.invalidateQueries({ queryKey: ['me'] });
    // Utilizator nou fără profil de afacere → onboarding; altfel dashboard.
    try {
      const bp = await api.get<{ name?: string | null }>('/api/v1/me/business-profile');
      navigate(bp?.name ? '/dashboard' : '/onboarding', { replace: true });
    } catch {
      navigate('/dashboard', { replace: true });
    }
  }

  async function requestOtp() {
    if (!email.trim()) {
      toast.push({ kind: 'warn', title: 'Introdu adresa de email' });
      return;
    }
    setBusy(true);
    try {
      await sendOtp(email.trim());
      setView('otp');
      setResendIn(45);
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => otpRefs.current[0]?.focus(), 60);
    } catch (err) {
      toast.push({ kind: 'err', title: 'Nu am putut trimite codul', body: humanError(err) });
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp() {
    const code = otp.join('');
    if (code.length < 6) {
      toast.push({ kind: 'warn', title: 'Introdu toate cele 6 cifre' });
      return;
    }
    setBusy(true);
    try {
      const res = await verifyOtp(email.trim(), code);
      if (res.twoFactorRedirect) {
        setView('twofa');
        return;
      }
      // Confirmă sesiunea.
      await getMe();
      await finishLogin();
    } catch (err) {
      toast.push({ kind: 'err', title: 'Cod greșit sau expirat', body: humanError(err) });
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp() {
    setBusy(true);
    try {
      await verifyTotp(totp);
      await finishLogin();
    } catch (err) {
      toast.push({ kind: 'err', title: 'Cod 2FA invalid', body: humanError(err) });
    } finally {
      setBusy(false);
    }
  }

  async function googleSignIn() {
    setBusy(true);
    try {
      const res = await api.post<{ url?: string; redirect?: boolean }>('/api/auth/sign-in/social', {
        provider: 'google',
        callbackURL: `${window.location.origin}/`,
      });
      if (res.url) {
        window.location.href = res.url;
      } else {
        toast.push({ kind: 'warn', title: 'Autentificarea cu Google nu este disponibilă momentan.' });
      }
    } catch {
      toast.push({ kind: 'warn', title: 'Autentificarea cu Google nu este configurată încă.' });
    } finally {
      setBusy(false);
    }
  }

  function setOtpDigit(i: number, raw: string) {
    const v = raw.replace(/\D/g, '').slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
    if (v && i < 5) otpRefs.current[i + 1]?.focus();
  }

  function onOtpPaste(e: React.ClipboardEvent) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length) {
      e.preventDefault();
      const arr = text.split('');
      setOtp(['', '', '', '', '', ''].map((_, i) => arr[i] ?? ''));
      otpRefs.current[Math.min(text.length, 5)]?.focus();
    }
  }

  const emailShown = email.trim() || 'ana@afacereata.ro';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '28px 16px' }}>
      <a href={SITE_URL} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--ink)', textDecoration: 'none', marginBottom: 26 }}>
        <Logo withWordmark />
      </a>

      <div className="card rv-in" data-screen="autentificare" style={{ width: 'min(420px, 100%)', padding: 28, borderRadius: 20 }}>
        {(view === 'login' || view === 'register') && (
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800 }}>{view === 'login' ? 'Bine ai revenit' : 'Creează-ți contul'}</h1>
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6 }}>
              {view === 'login'
                ? 'Intri fără parolă: primești un cod pe email sau folosești contul Google.'
                : 'Durează sub un minut. Primul preview e gratuit — plătești doar clipurile pe care le accepți.'}
            </p>
            <button className="btn btn-ghost" onClick={googleSignIn} disabled={busy} style={{ marginTop: 20, width: '100%', padding: 14, fontSize: 15 }}>
              <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--line)', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12, color: 'var(--accent-text)' }}>
                G
              </span>
              {view === 'login' ? 'Continuă cu Google' : 'Înregistrare cu Google'}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
              <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              <span style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--muted)' }}>sau cu emailul</span>
              <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            </div>
            {view === 'register' && (
              <label className="label" style={{ marginBottom: 12 }}>
                Numele tău
                <input className="input" placeholder="Ana Ionescu" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
            )}
            <label className="label">
              Adresa de email
              <input
                className="input"
                type="email"
                inputMode="email"
                data-testid="email-input"
                placeholder="ana@afacereata.ro"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && requestOtp()}
              />
            </label>
            {view === 'register' && (
              <label style={{ display: 'flex', gap: 9, marginTop: 14, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55, cursor: 'pointer' }}>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
                Sunt de acord cu <a href={`${SITE_URL}/termeni`} style={{ fontWeight: 800 }}>termenii</a> și{' '}
                <a href={`${SITE_URL}/confidentialitate`} style={{ fontWeight: 800 }}>politica de confidențialitate</a>.
              </label>
            )}
            <button className="btn btn-primary" data-testid="send-code" onClick={requestOtp} disabled={busy || (view === 'register' && !agree)} style={{ marginTop: 14, width: '100%', padding: 15, fontSize: 15.5 }}>
              {busy ? <Spinner /> : view === 'login' ? 'Trimite-mi codul' : 'Creează contul'}
            </button>
            {view === 'login' && (
              <>
                <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
                  Fără parole de ținut minte — primești un cod de unică folosință.
                </p>
                <button
                  onClick={() => {
                    toast.push({ kind: 'info', title: 'Passkey', body: 'Confirmă cu amprenta/Face ID din fereastra browserului.' });
                    setView('passkey');
                  }}
                  className="btn"
                  style={{ marginTop: 16, width: '100%', border: '1.5px dashed var(--line)', padding: 13, fontSize: 13.5, color: 'var(--muted)' }}
                >
                  <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', border: '2.5px solid currentColor' }} />
                  Intră cu passkey (amprentă / Face ID)
                </button>
              </>
            )}
          </div>
        )}

        {view === 'otp' && (
          <div>
            <button onClick={() => setView('login')} style={{ fontSize: 13, fontWeight: 800, color: 'var(--muted)' }}>
              ← Alt email
            </button>
            <h1 style={{ margin: '14px 0 0', fontSize: 24, fontWeight: 800 }}>Verifică-ți emailul</h1>
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6 }}>
              Am trimis un cod din 6 cifre la <strong style={{ color: 'var(--ink)' }}>{emailShown}</strong>. E valabil 5 minute.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'space-between' }} onPaste={onOtpPaste}>
              {otp.map((v, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  inputMode="numeric"
                  maxLength={1}
                  value={v}
                  data-testid={`otp-${i}`}
                  aria-label={`Cifra ${i + 1}`}
                  onChange={(e) => setOtpDigit(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !v && i > 0) otpRefs.current[i - 1]?.focus();
                    if (e.key === 'Enter') submitOtp();
                  }}
                  style={{ width: '100%', minWidth: 0, aspectRatio: '4 / 5', textAlign: 'center', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 800, fontSize: 22, border: `1.5px solid ${v ? 'var(--accent)' : 'var(--line)'}`, borderRadius: 11, background: 'var(--bg)' }}
                />
              ))}
            </div>
            <button className="btn btn-primary" data-testid="verify-otp" onClick={submitOtp} disabled={busy} style={{ marginTop: 18, width: '100%', padding: 15, fontSize: 15.5 }}>
              {busy ? <Spinner /> : 'Verifică și intră'}
            </button>
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--muted)', textAlign: 'center' }}>
              N-a ajuns? Caută în Spam sau{' '}
              <button disabled={resendIn > 0} onClick={requestOtp} style={{ fontWeight: 800, color: resendIn > 0 ? 'var(--muted)' : 'var(--accent-text)' }}>
                retrimite codul
              </button>{' '}
              {resendIn > 0 && <span className="tnum">({formatMmSs(resendIn)})</span>}
            </p>
          </div>
        )}

        {view === 'twofa' && (
          <div>
            <span className="pill" style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)' }} />
              Cod email corect
            </span>
            <h1 style={{ margin: '14px 0 0', fontSize: 24, fontWeight: 800 }}>Încă un pas: 2FA</h1>
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6 }}>
              Contul tău are verificarea în doi pași activată. Introdu codul din aplicația de autentificare.
            </p>
            <input
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              value={totp}
              data-testid="totp-input"
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ marginTop: 18, width: '100%', textAlign: 'center', letterSpacing: '.4em', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 800, fontSize: 24, border: '1.5px solid var(--line)', borderRadius: 12, padding: 14, background: 'var(--bg)' }}
            />
            <button className="btn btn-primary" onClick={submitTotp} disabled={busy} style={{ marginTop: 14, width: '100%', padding: 15, fontSize: 15.5 }}>
              {busy ? <Spinner /> : 'Confirmă și intră'}
            </button>
          </div>
        )}

        {view === 'passkey' && (
          <div style={{ textAlign: 'center' }}>
            <span aria-hidden="true" style={{ display: 'inline-grid', placeItems: 'center', width: 64, height: 64, borderRadius: '50%', background: 'var(--accent-soft)', color: 'var(--accent-text)', marginTop: 6 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', border: '3px solid currentColor' }} />
            </span>
            <h1 style={{ margin: '16px 0 0', fontSize: 24, fontWeight: 800 }}>Confirmă cu passkey</h1>
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6 }}>
              Folosește amprenta, Face ID sau PIN-ul dispozitivului. Browserul îți arată fereastra de confirmare.
            </p>
            <button onClick={() => setView('login')} style={{ marginTop: 14, fontSize: 13, fontWeight: 800, color: 'var(--muted)' }}>
              ← Înapoi la email
            </button>
          </div>
        )}
      </div>

      <p style={{ margin: '18px 0 0', fontSize: 13, color: 'var(--muted)' }}>
        {view === 'register' ? (
          <>
            Ai deja cont?{' '}
            <button onClick={() => setView('login')} style={{ fontWeight: 800, color: 'var(--accent-text)' }}>
              Intră în cont
            </button>
          </>
        ) : (
          <>
            Nu ai cont?{' '}
            <button data-testid="to-register" onClick={() => setView('register')} style={{ fontWeight: 800, color: 'var(--accent-text)' }}>
              Înregistrează-te gratuit
            </button>
          </>
        )}
      </p>
      <a href={SITE_URL} style={{ marginTop: 10, fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', textDecoration: 'none' }}>
        ← înapoi la site
      </a>
    </div>
  );
}
